import { useState, useEffect, useCallback, useRef } from 'react';

export default function usePaginatedDataLocal(
  serviceFunction,
  { limit: initialLimit = 1000, extraPromise } = {}
) {
  const [cache, setCache] = useState({});
  const [data, setData] = useState({
    results: [],
    count: 0,
    next: null,
    previous: null,
  });
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(initialLimit);
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * limit;
  const totalPages = Math.ceil(data.count / limit);

  const ignoreCache = useRef(false);

  useEffect(() => {
	setCache({});
	setCurrentPage(1);
    ignoreCache.current = true;
  }, [serviceFunction]);

  useEffect(() => {
	if (initialLimit !== limit) {
	  setLimit(initialLimit);
	  setCache({});
	  setCurrentPage(1);
	}
  }, [initialLimit, limit]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (extraPromise) {
          await extraPromise;
        }

        const shouldIgnoreCache = ignoreCache.current;
        if (shouldIgnoreCache) {
            ignoreCache.current = false;
        }

        if (!shouldIgnoreCache && cache[offset]) {
          setData(cache[offset]);
        } else {
          setLoading(true);
          const queryString = `?limit=${limit}&offset=${offset}`;
          const response = await serviceFunction(queryString);
          const newData = {
            results: response.results || [],
            count: response.count || 0,
            next: response.next,
            previous: response.previous,
          };
          setCache(prev => ({ ...prev, [offset]: newData }));
          setData(newData);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const prefetchNextPage = async () => {
      if (currentPage < totalPages) {
        const nextOffset = currentPage * limit;
        if (!cache[nextOffset]) {
          try {
            const nextQueryString = `?limit=${limit}&offset=${nextOffset}`;
            const response = await serviceFunction(nextQueryString);
            const nextData = {
              results: response.results || [],
              count: response.count || 0,
              next: response.next,
              previous: response.previous,
            };
            setCache(prev => ({ ...prev, [nextOffset]: nextData }));
          } catch (error) {
            console.error(error);
          }
        }
      }
    };

    fetchData();
    prefetchNextPage();
  }, [offset, currentPage, limit, extraPromise, serviceFunction]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const startIndex = offset + 1;
  const endIndex = offset + data.results.length;

  return { data, loading, currentPage, totalPages, handlePageChange, startIndex, endIndex };
}
